<template>
  <div id="app">

    <!-- ── Navbar ── -->
    <nav class="navbar" :class="{ 'navbar--scrolled': scrolled }">
      <div class="navbar__inner">
        <a class="navbar__brand" href="#">
          <span class="navbar__brand-icon"><i class="fas fa-bolt"></i></span>
          pmelectrical.ca
        </a>

        <button
          class="navbar__burger"
          :class="{ 'is-active': menuOpen }"
          @click="menuOpen = !menuOpen"
          aria-label="Toggle navigation"
          id="nav-burger-btn"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div class="navbar__menu" :class="{ 'is-open': menuOpen }">
          <a class="navbar__link" href="/" @click="menuOpen = false">Home</a>
        </div>
      </div>
    </nav>

    <!-- ── Page content ── -->
    <div class="page-content">
      <router-view/>
    </div>

    <!-- ── Footer ── -->
    <footer class="site-footer">
      <div class="site-footer__inner">
        <p>
          <a href="mailto: contact@pmelectrical.ca">Contact Us</a>
          <span class="site-footer__sep">|</span>
          &copy; 2022 Prime Mover Electrical
          <span class="site-footer__sep">|</span>
          site by <a href="https://topsailsoftware.ca" target="new">Topsail Software Inc.</a>
        </p>
      </div>
    </footer>

  </div>
</template>

<script>
export default {
  name: 'App',
  data () {
    return {
      scrolled: false,
      menuOpen: false
    }
  },
  mounted () {
    window.addEventListener('scroll', this.onScroll)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    onScroll () {
      this.scrolled = window.scrollY > 20
    }
  }
}
</script>

<style>
/* ── Reset & base ── */
*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html {
  scroll-behavior: smooth;
}

body {
  background-color: #0d0d0d;
  color: #f0f0f0;
  font-family: 'Inter', 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
}

a {
  color: #FFD600;
  text-decoration: none;
  transition: color 0.2s ease, opacity 0.2s ease;
}
a:hover {
  color: #ffe94d;
  opacity: 0.9;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
}

/* ── Navbar ── */
.navbar {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(10, 10, 10, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 214, 0, 0.08);
  transition: background 0.3s ease, box-shadow 0.3s ease;
}

.navbar--scrolled {
  background: rgba(8, 8, 8, 0.97);
  box-shadow: 0 2px 24px rgba(0, 0, 0, 0.6);
}

.navbar__inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.25rem;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.navbar__brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: #f0f0f0;
  letter-spacing: -0.01em;
  text-decoration: none;
}
.navbar__brand:hover {
  color: #FFD600;
  opacity: 1;
}

.navbar__brand-icon {
  color: #FFD600;
  font-size: 1rem;
}

.navbar__menu {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.navbar__link {
  color: #ccc;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  text-decoration: none;
  transition: color 0.2s ease, background 0.2s ease;
}
.navbar__link:hover {
  color: #FFD600;
  background: rgba(255, 214, 0, 0.08);
  opacity: 1;
}

/* Hamburger */
.navbar__burger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: background 0.2s;
}
.navbar__burger:hover {
  background: rgba(255, 214, 0, 0.1);
}
.navbar__burger span {
  display: block;
  width: 100%;
  height: 2px;
  background: #f0f0f0;
  border-radius: 2px;
  transition: all 0.3s ease;
  transform-origin: center;
}
.navbar__burger.is-active span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}
.navbar__burger.is-active span:nth-child(2) {
  opacity: 0;
  transform: scaleX(0);
}
.navbar__burger.is-active span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* ── Footer ── */
.site-footer {
  background: #080808;
  border-top: 1px solid rgba(255, 214, 0, 0.15);
  padding: 1.25rem 1rem;
  margin-top: auto;
}

.site-footer__inner {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  font-size: 0.82rem;
  color: #888;
}

.site-footer__inner a {
  color: #aaa;
}
.site-footer__inner a:hover {
  color: #FFD600;
}

.site-footer__sep {
  margin: 0 0.5rem;
  opacity: 0.4;
}

/* ── Mobile ── */
@media (max-width: 767px) {
  .navbar__burger {
    display: flex;
  }

  .navbar__menu {
    display: none;
    position: absolute;
    top: 60px;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    background: rgba(10, 10, 10, 0.98);
    border-bottom: 1px solid rgba(255, 214, 0, 0.12);
    padding: 0.5rem 1rem 1rem;
    gap: 0.25rem;
  }

  .navbar__menu.is-open {
    display: flex;
  }

  .navbar__link {
    font-size: 1rem;
    padding: 0.65rem 0.75rem;
  }
}
</style>
